import { useState } from "react";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { ArrowLeft2, ArrowRight2 } from "iconsax-react";
import { Button, PromptForm, Prompt } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import { useDeleteMsbCompletionActMutation } from "../../api/msb-contract";
import type { MsbCompletionAct } from "../../types/msb";
import MsbBigActCard from "./MsbBigActCard";
import MsbActCard from "./MsbActCard";
import MsbActDetailModal from "./MsbActDetailModal";

interface Props {
  acts: MsbCompletionAct[];
  locale: "ru" | "kk" | "en";
  onEdit?: (act: MsbCompletionAct) => void;
}

const FIRST_PAGE_SIZE = 4;
const REGULAR_PAGE_SIZE = 6;

function getPageActs(acts: MsbCompletionAct[], page: number): MsbCompletionAct[] {
  if (page === 0) {
    return acts.slice(0, FIRST_PAGE_SIZE);
  }
  const offset = FIRST_PAGE_SIZE + (page - 1) * REGULAR_PAGE_SIZE;
  return acts.slice(offset, offset + REGULAR_PAGE_SIZE);
}

function getTotalPages(total: number): number {
  if (total <= FIRST_PAGE_SIZE) return 1;
  return 1 + Math.ceil((total - FIRST_PAGE_SIZE) / REGULAR_PAGE_SIZE);
}

export default function MsbHistoryBlock({ acts, locale, onEdit }: Props) {
  const { t } = useTranslation("EmployeePortal");
  const [page, setPage] = useState(0);
  const [selectedAct, setSelectedAct] = useState<MsbCompletionAct | null>(null);
  const [confirmDeleteAct, setConfirmDeleteAct] = useState<MsbCompletionAct | null>(null);
  const [deleteCompletionAct] = useDeleteMsbCompletionActMutation();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  const totalPages = getTotalPages(acts.length);
  const pageActs = getPageActs(acts, page);

  function handlePrev() {
    setPage((p) => Math.max(0, p - 1));
  }

  function handleNext() {
    setPage((p) => Math.min(totalPages - 1, p + 1));
  }

  function handleEditFromModal(act: MsbCompletionAct) {
    setSelectedAct(null);
    onEdit?.(act);
  }

  async function handleDeleteConfirm() {
    if (!confirmDeleteAct || deletingId !== null) return;
    setDeletingId(confirmDeleteAct.id);
    try {
      await deleteCompletionAct(confirmDeleteAct.id).unwrap();
      setConfirmDeleteAct(null);
      setSelectedAct(null);
      setPrompt({
        title: t("calculations.actDetail.deleteSuccessTitle"),
        text: t("calculations.actDetail.deleteSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setConfirmDeleteAct(null);
      setPrompt({
        title: t("calculations.modal.errorTitle"),
        text: extractErrorMessage(err, "calculations.actDetail.deleteError", t),
        variant: "error",
      });
    } finally {
      setDeletingId(null);
    }
  }

  if (acts.length === 0) {
    return (
      <div className="flex flex-col gap-5 p-5 radius-lg surface-base-fill">
        <h3 className="text-body-bold-lg content-base-primary">{t("calculations.historyTitle")}</h3>
        <p className="text-body-regular-md content-action-neutral">{t("calculations.noActs")}</p>
      </div>
    );
  }

  function getShownRange(): { from: number; to: number } {
    if (page === 0) {
      return { from: 1, to: Math.min(FIRST_PAGE_SIZE, acts.length) };
    }
    const from = FIRST_PAGE_SIZE + (page - 1) * REGULAR_PAGE_SIZE + 1;
    const to = Math.min(from + REGULAR_PAGE_SIZE - 1, acts.length);
    return { from, to };
  }

  const { from, to } = getShownRange();

  return (
    <>
      <div className="p-5 radius-3xl surface-base-fill">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-body-bold-lg content-base-primary">{t("calculations.historyTitle")}</h3>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <p className="text-label-xs content-action-neutral">
                {t("calculations.history.shown", { from, to, total: acts.length })}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  isIconButton
                  onClick={handlePrev}
                  disabled={page === 0}
                  className="w-8! h-8!">
                  <ArrowLeft2 size={16} color="currentColor" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  isIconButton
                  onClick={handleNext}
                  disabled={page >= totalPages - 1}
                  className="w-8! h-8!">
                  <ArrowRight2 size={16} color="currentColor" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {page === 0 ? (
          <FeaturedLayout acts={pageActs} locale={locale} onActClick={setSelectedAct} />
        ) : (
          <RegularGridLayout acts={pageActs} locale={locale} onActClick={setSelectedAct} />
        )}

        {selectedAct && (
          <MsbActDetailModal
            act={selectedAct}
            locale={locale}
            onClose={() => setSelectedAct(null)}
            onEdit={handleEditFromModal}
          />
        )}
      </div>

      {confirmDeleteAct && (
        <PromptForm
          title={t("calculations.actDetail.deleteConfirmTitle")}
          text={t("calculations.actDetail.deleteConfirmText")}
          variant="error"
          onClose={() => setConfirmDeleteAct(null)}
          onConfirm={handleDeleteConfirm}
          isLoading={deletingId !== null}
          confirmText={t("calculations.actDetail.deleteConfirm")}
          cancelText={t("calculations.modal.cancel")}
          namespace="EmployeePortal"
        />
      )}

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="EmployeePortal"
        />
      )}
    </>
  );
}

interface LayoutProps {
  acts: MsbCompletionAct[];
  locale: "ru" | "kk" | "en";
  onActClick: (act: MsbCompletionAct) => void;
}

function FeaturedLayout({ acts, locale, onActClick }: LayoutProps) {
  if (acts.length === 0) return null;

  const [featured, ...rest] = acts;

  return (
    <div className={cn("grid grid-cols-2 gap-4", rest.length > 0 && "auto-rows-fr")}>
      <div className={cn(rest.length > 0 && "row-span-3")}>
        <MsbBigActCard act={featured} locale={locale} onClick={() => onActClick(featured)} />
      </div>
      {rest.map((act) => (
        <MsbActCard key={act.id} act={act} locale={locale} onClick={() => onActClick(act)} />
      ))}
    </div>
  );
}

function RegularGridLayout({ acts, locale, onActClick }: LayoutProps) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {acts.map((act) => (
        <MsbActCard key={act.id} act={act} locale={locale} onClick={() => onActClick(act)} />
      ))}
    </div>
  );
}
